import axios from "axios";
import axiosInstance from "../api/axios-config";

export interface LocationCoordinate {
  latitude: number;
  longitude: number;
}

export interface GeocodingResult {
  address: string;
  streetNumber?: string;
  street?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  formattedAddress?: string;
  placeId?: string;
}

const STADIA_API_KEY = process.env.REACT_APP_STADIA_API_KEY;
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_APIKEY;

/**
 * Fonction pour formater le résultat du géocodage inverse Google
 */
export const formatGeocoderResult = (result: any): GeocodingResult => {
  // Vérifier si le résultat existe et a une propriété geometry
  if (!result || !result.geometry) {
    console.warn("Invalid geocoder result received:", result);
    return { address: "Adresse inconnue" };
  }

  // Extraire les composants d'adresse
  let streetNumber = "";
  let street = "";
  let city = "";
  let postalCode = "";
  let country = "";

  if (result.address_components) {
    for (const component of result.address_components) {
      const types = component.types;

      if (types.includes("street_number")) {
        streetNumber = component.long_name;
      } else if (types.includes("route")) {
        street = component.long_name;
      } else if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("postal_code")) {
        postalCode = component.long_name;
      } else if (types.includes("country")) {
        country = component.long_name;
      }
    }
  }

  return {
    address: result.formatted_address || "Adresse inconnue",
    streetNumber,
    street,
    city,
    country,
    postalCode,
    formattedAddress: result.formatted_address,
    placeId: result.place_id,
  };
};

/**
 * Fonction pour formater le résultat du géocodage inverse Stadia/Pelias
 */
const formatStadiaResult = (feature: any): GeocodingResult => {
  if (!feature || !feature.properties) {
    return { address: "Adresse inconnue" };
  }

  const props = feature.properties;

  return {
    address: props.label || "Adresse inconnue",
    streetNumber: props.housenumber || "",
    street: props.street || "",
    city: props.locality || props.city || "",
    country: props.country || "",
    postalCode: props.postalcode || "",
    formattedAddress: props.label,
    placeId: feature.properties.gid,
  };
};

/**
 * Reverse geocoding avec Stadia Maps (Pelias)
 */
const reverseGeocodingStadia = async (
  latitude: number,
  longitude: number
): Promise<GeocodingResult | null> => {
  if (!STADIA_API_KEY) {
    return null;
  }

  try {
    const url = `https://api.stadiamaps.com/geocoding/v1/reverse?point.lat=${latitude}&point.lon=${longitude}&api_key=${STADIA_API_KEY}`;
    const response = await axios.get(url);

    if (response.data?.features?.length > 0) {
      return formatStadiaResult(response.data.features[0]);
    }
    return null;
  } catch (error) {
    console.warn("Stadia geocoding failed:", error);
    return null;
  }
};

/**
 * Reverse geocoding avec Google Maps
 */
const reverseGeocodingGoogle = async (
  latitude: number,
  longitude: number
): Promise<GeocodingResult | null> => {
  if (!GOOGLE_API_KEY) {
    return null;
  }

  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_API_KEY}`;
    const response = await axios.get(url);

    if (
      response.data?.status === "OK" &&
      response.data?.results?.length > 0
    ) {
      return formatGeocoderResult(response.data.results[0]);
    }
    return null;
  } catch (error) {
    console.warn("Google geocoding failed:", error);
    return null;
  }
};

/**
 * Reverse geocoding via le backend (proxy vers Nominatim)
 * Évite les problèmes CORS du navigateur
 */
const reverseGeocodingBackend = async (
  latitude: number,
  longitude: number
): Promise<GeocodingResult | null> => {
  try {
    const response = await axiosInstance.get(`/location/geocode/reverse?lat=${latitude}&lng=${longitude}`);
    if (response.data?.address) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.warn("Backend geocoding failed:", error);
    return null;
  }
};

/**
 * Fonction pour obtenir une adresse à partir de coordonnées
 * Utilise le backend qui appelle Stadia, Google Maps et Nominatim en parallèle
 * et sélectionne le résultat le plus précis (avec rue)
 */
export const reverseGeocoding = async (
  latitude: number,
  longitude: number
): Promise<GeocodingResult> => {
  if (!latitude || !longitude) {
    return { address: "" };
  }

  // Le backend fait déjà le travail d'appeler les 3 providers en parallèle
  // et de sélectionner le meilleur résultat (le plus précis avec rue)
  const backendResult = await reverseGeocodingBackend(latitude, longitude);
  if (backendResult && backendResult.address) {
    return backendResult;
  }

  // Fallback direct sur Stadia si le backend échoue
  const stadiaResult = await reverseGeocodingStadia(latitude, longitude);
  if (stadiaResult && stadiaResult.address) {
    return stadiaResult;
  }

  // Fallback direct sur Google si Stadia échoue aussi
  const googleResult = await reverseGeocodingGoogle(latitude, longitude);
  if (googleResult && googleResult.address) {
    return googleResult;
  }

  return { address: "" };
};

/**
 * Fonction pour obtenir des coordonnées à partir d'une adresse
 */
export const forwardGeocoding = async (
  address: string
): Promise<LocationCoordinate | null> => {
  try {
    if (!address) {
      throw new Error("Address is required");
    }

    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${GOOGLE_API_KEY}`;
    const response = await axios.get(url);

    if (
      response.data &&
      response.data.status === "OK" &&
      response.data.results &&
      response.data.results.length > 0 &&
      response.data.results[0].geometry &&
      response.data.results[0].geometry.location
    ) {
      const location = response.data.results[0].geometry.location;
      return {
        latitude: location.lat,
        longitude: location.lng,
      };
    }

    console.warn("Forward geocoding API returned no results:", response.data);
    return null;
  } catch (error) {
    console.error("Error during forward geocoding:", error);
    return null;
  }
};
